import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatMoney, isAdmin } from '@vns/core';
import { supabase } from './supabase';
import { useSession } from './session';
import { store } from '../platform';

/**
 * Notifications & Payment Reminders.
 *
 * Automatically tracks projects where money is still pending (e.g. Budget ₹45L, Paid ₹20L -> Balance ₹25L).
 * Generates payment_due reminders based on the company's reminder interval (e.g. 7 days, 14 days, 21 days, 30 days).
 * When a project is fully paid (balance <= 0), reminders automatically stop and active ones are cleared.
 */

export type NotificationKind =
  | 'payment_due' | 'approval_pending' | 'entry_approved' | 'entry_rejected';

export interface NotificationRow {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  projectId: string | null;
  projectName: string | null;
  clientName: string | null;
  projectValuePaise: number | null;
  paidAmountPaise: number | null;
  amountPaise: number | null;
  readAt: string | null;
  createdAt: string;
}

/** Headings for the notifications screen — grouped cleanly with clear visual sections. */
export const KIND_META: Record<NotificationKind, { heading: string; blurb: string }> = {
  payment_due: {
    heading: 'Pending Client Payments',
    blurb: 'Projects with an outstanding balance awaiting collection',
  },
  approval_pending: {
    heading: 'Waiting for Your Approval',
    blurb: 'Site purchases, usage, labour, expenses, and transfers submitted by staff',
  },
  entry_approved: {
    heading: 'Your Approved Submissions',
    blurb: 'Site entries you submitted that have been approved by admin',
  },
  entry_rejected: {
    heading: 'Returned / Rejected Submissions',
    blurb: 'Entries returned for correction with review feedback',
  },
};

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

/**
 * Sync payment due reminders for admins:
 * - Checks all active projects with pending balance (value_paise - paid > 0).
 * - Creates payment_due reminder if none exists within the company's reminder_days interval.
 * - Auto-dismisses old reminders when a project is fully paid up.
 */
async function syncPaymentDueReminders(companyId?: string | null) {
  if (!companyId) return;

  try {
    // 1. Fetch company reminder_days
    const { data: comp } = await supabase
      .from('companies')
      .select('reminder_days')
      .eq('id', companyId)
      .single();
    const reminderDays = comp?.reminder_days ?? 21;

    // 2. Fetch live projects
    const { data: projects } = await supabase
      .from('projects')
      .select('id, name, value_paise, status, client_id, clients(name)')
      .is('deleted_at', null)
      .in('status', ['ongoing', 'on_hold', 'planning']);

    if (!projects || !projects.length) return;

    // 3. Fetch approved payments
    const { data: payments } = await supabase
      .from('client_payments')
      .select('project_id, amount_paise')
      .eq('approval_status', 'approved')
      .is('deleted_at', null);

    const paidByProject = new Map<string, number>();
    for (const p of payments ?? []) {
      paidByProject.set(p.project_id, (paidByProject.get(p.project_id) ?? 0) + Number(p.amount_paise));
    }

    // 4. Fetch existing payment_due notifications
    const { data: existingReminders } = await supabase
      .from('notifications')
      .select('id, project_id, created_at, dismissed_at')
      .eq('kind', 'payment_due');

    const recentRemindersByProject = new Map<string, { id: string; createdAt: string; dismissed: boolean }[]>();
    for (const r of existingReminders ?? []) {
      if (!r.project_id) continue;
      const list = recentRemindersByProject.get(r.project_id) ?? [];
      list.push({ id: r.id, createdAt: r.created_at, dismissed: !!r.dismissed_at });
      recentRemindersByProject.set(r.project_id, list);
    }

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - reminderDays);
    const cutoffIso = cutoffDate.toISOString();

    const toInsert: Array<{
      company_id: string;
      kind: 'payment_due';
      title: string;
      body: string;
      project_id: string;
      amount_paise: number;
    }> = [];

    const toDismiss: string[] = [];

    for (const p of projects) {
      const paid = paidByProject.get(p.id) ?? 0;
      const balance = Number(p.value_paise) - paid;
      const clientName = (p.clients as unknown as { name?: string } | null)?.name ?? 'Client';
      const reminders = recentRemindersByProject.get(p.id) ?? [];

      if (balance <= 0) {
        // Fully paid — auto-dismiss any active notifications
        for (const rem of reminders) {
          if (!rem.dismissed) toDismiss.push(rem.id);
        }
      } else {
        // Has pending balance: check if a reminder was created recently (within reminder_days)
        const hasRecentReminder = reminders.some((r) => r.createdAt > cutoffIso);
        if (!hasRecentReminder) {
          toInsert.push({
            company_id: companyId,
            kind: 'payment_due',
            title: `${clientName} — payment pending`,
            body: `${p.name} has an outstanding balance of ${formatMoney(balance)}.`,
            project_id: p.id,
            amount_paise: balance,
          });
        }
      }
    }

    if (toDismiss.length > 0) {
      await supabase
        .from('notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .in('id', toDismiss);
    }

    if (toInsert.length > 0) {
      await supabase.from('notifications').insert(toInsert);
    }
  } catch {
    // Non-blocking sync failure fallback
  }
}

/**
 * Fetch notifications with project, client, budget, and payment details.
 * Synthesizes active payment reminders for all pending projects so reminders
 * are never missing, while syncing with database notifications.
 */
export function useNotifications() {
  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);

  return useQuery({
    queryKey: ['notifications', actor?.id ?? ''],
    enabled: !!actor,
    refetchInterval: 30_000,
    queryFn: async (): Promise<NotificationRow[]> => {
      // Query existing DB notifications, active projects, and payments
      const [notifsRes, projectsRes, paymentsRes] = await Promise.all([
        supabase
          .from('notifications')
          .select('id, kind, title, body, project_id, amount_paise, read_at, dismissed_at, created_at, projects(id, name, value_paise, clients(name))')
          .is('dismissed_at', null)
          .order('created_at', { ascending: false })
          .limit(100),
        admin
          ? supabase
              .from('projects')
              .select('id, name, value_paise, status, client_id, clients(name)')
              .is('deleted_at', null)
              .in('status', ['ongoing', 'on_hold'])
          : Promise.resolve({ data: [] as any[], error: null }),
        admin
          ? supabase
              .from('client_payments')
              .select('project_id, amount_paise')
              .eq('approval_status', 'approved')
              .is('deleted_at', null)
          : Promise.resolve({ data: [] as any[], error: null }),
      ]);

      const dbRows = notifsRes.data ?? [];
      const paidByProj = new Map<string, number>();
      for (const p of paymentsRes.data ?? []) {
        paidByProj.set(p.project_id, (paidByProj.get(p.project_id) ?? 0) + Number(p.amount_paise));
      }

      const results: NotificationRow[] = [];
      const seenProjects = new Set<string>();

      // A. Process DB notifications
      for (const r of dbRows) {
        const proj = r.projects as unknown as {
          id: string;
          name: string;
          value_paise: number;
          clients: { name: string } | null;
        } | null;

        const projId = r.project_id;
        if (projId) seenProjects.add(projId);

        const val = proj?.value_paise ? Number(proj.value_paise) : null;
        const paid = projId ? paidByProj.get(projId) ?? 0 : null;
        const liveBalance = (val != null && paid != null) ? Math.max(0, val - paid) : r.amount_paise;

        // Check local read override if not set in DB
        const localRead = store.get(`notif-read-${r.id}`);
        const isRead = r.read_at || localRead;

        // Skip if payment_due is already fully paid (0 balance)
        if (r.kind === 'payment_due' && liveBalance != null && liveBalance <= 0) {
          continue;
        }

        results.push({
          id: r.id,
          kind: r.kind as NotificationKind,
          title: r.title,
          body: r.body,
          projectId: projId,
          projectName: proj?.name ?? null,
          clientName: proj?.clients?.name ?? null,
          projectValuePaise: val,
          paidAmountPaise: paid,
          amountPaise: r.kind === 'payment_due' ? liveBalance : r.amount_paise,
          readAt: isRead ? (r.read_at ?? localRead ?? new Date().toISOString()) : null,
          createdAt: r.created_at,
        });
      }

      // B. If admin, check all projects for pending balances not yet in DB notifications
      if (admin && projectsRes.data) {
        for (const p of projectsRes.data) {
          if (seenProjects.has(p.id)) continue;
          const paid = paidByProj.get(p.id) ?? 0;
          const val = Number(p.value_paise);
          const balance = val - paid;

          if (balance > 0) {
            const virtualId = `proj-rem-${p.id}`;
            const isDismissed = store.get(`notif-dismiss-${virtualId}`);
            if (isDismissed) continue;

            const localRead = store.get(`notif-read-${virtualId}`);
            const clientName = (p.clients as unknown as { name?: string } | null)?.name ?? 'Client';

            results.push({
              id: virtualId,
              kind: 'payment_due',
              title: `${clientName} — payment pending`,
              body: `${p.name} has an outstanding balance of ${formatMoney(balance)}.`,
              projectId: p.id,
              projectName: p.name,
              clientName,
              projectValuePaise: val,
              paidAmountPaise: paid,
              amountPaise: balance,
              readAt: localRead ?? null,
              createdAt: new Date().toISOString(),
            });
          }
        }
      }

      return results;
    },
  });
}

export function useUnreadCount() {
  const { data } = useNotifications();
  return (data ?? []).filter((n) => !n.readAt).length;
}

/** Mark a single notification as read. */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Store local read timestamp
      store.set(`notif-read-${id}`, new Date().toISOString());

      // If it's a real DB UUID, update DB
      if (!id.startsWith('proj-rem-')) {
        try {
          await supabase
            .from('notifications')
            .update({ read_at: new Date().toISOString() })
            .eq('id', id);
        } catch {
          // ignore
        }
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/** Dismissing hides it for good until the next reminder interval if still unpaid. */
export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      store.set(`notif-dismiss-${id}`, new Date().toISOString());

      if (!id.startsWith('proj-rem-')) {
        try {
          await supabase
            .from('notifications')
            .update({ dismissed_at: new Date().toISOString() })
            .eq('id', id);
        } catch {
          // ignore
        }
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!ids.length) return;
      const now = new Date().toISOString();
      for (const id of ids) {
        store.set(`notif-read-${id}`, now);
      }

      const realIds = ids.filter((id) => !id.startsWith('proj-rem-'));
      if (realIds.length > 0) {
        try {
          await supabase
            .from('notifications')
            .update({ read_at: now })
            .in('id', realIds);
        } catch {
          // ignore
        }
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/** Dismiss everything currently shown, for "clear all". */
export function useDismissAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!ids.length) return;
      const now = new Date().toISOString();
      for (const id of ids) {
        store.set(`notif-dismiss-${id}`, now);
      }

      const realIds = ids.filter((id) => !id.startsWith('proj-rem-'));
      if (realIds.length > 0) {
        try {
          await supabase
            .from('notifications')
            .update({ dismissed_at: now })
            .in('id', realIds);
        } catch {
          // ignore
        }
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

